/**
 * Collabs Slice
 *
 * iPhone mockup carousel with synced text and video or photo per slide.
 * Stats shown as buttons under copy.
 */

import React from "react";
import { graphql } from "gatsby";
import { motion, AnimatePresence } from "framer-motion";
import { PrismicRichText } from "@prismicio/react";

import PhoneCarousel from "../../components/PhoneCarousel";
import CollabSlide from "../../components/CollabSlide";
import BackgroundStaticVideo from "../../components/BackgroundStaticVideo";
import {
  ActiveSlideProvider,
  useActiveSlide,
} from "../../context/active-slide-context";

import * as styles from "./Collabs.module.scss";

const CollabsContent = ({ items, color_scheme, show_background_static_video }) => {
  const { activeIndex } = useActiveSlide();
  const currentItem = items[activeIndex] || items[0];

  const textVariants = {
    enter: {
      opacity: 0,
      y: 20,
    },
    center: {
      opacity: 1,
      y: 0,
    },
    exit: {
      opacity: 0,
      y: -20,
    },
  };

  const hasMedia = items.some(
    item => (item.video_url && item.video_url.trim()) || (item.image?.url)
  );

  return (
    <section
      className={styles.collabs}
      data-color-scheme={color_scheme || "black"}
      data-background-static-video={show_background_static_video ? "true" : undefined}
      id="collabs"
    >
      {show_background_static_video && <BackgroundStaticVideo />}
      <div className={`page-width--slim padding-section-vertical ${styles.inner}`}>
        <div className={styles.copy}>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              variants={textVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
            >
            <h4 className={styles.caseStudy}>Case Study</h4>
              {currentItem?.heading?.richText && (
                <PrismicRichText
                  field={currentItem.heading.richText}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className={styles.heading}>{children}</h2>
                    ),
                  }}
                />
              )}
              {currentItem?.copy?.richText && (
                <div className={styles.copyText}>
                  <PrismicRichText field={currentItem.copy.richText} />
                </div>
              )}
              {(currentItem?.stat_1 || currentItem?.stat_2 || currentItem?.stat_3) && (
                <div className={styles.stats}>
                  {[currentItem.stat_1, currentItem.stat_2, currentItem.stat_3]
                    .filter(Boolean)
                    .map((label, i) => (
                      <span key={i} className={styles.stat}>
                        {label}
                      </span>
                    ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>

        </div>

        {hasMedia && (
          <motion.div
            className={styles.carousel}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <PhoneCarousel autoplay>
              {items.map((item, index) => {
                const hasVideo = item.video_url && item.video_url.trim();
                const hasImage = item.image?.url;
                if (!hasVideo && !hasImage) return null;
                return (
                  <CollabSlide
                    key={index}
                    index={index}
                    video_url={item.video_url}
                    image={item.image}
                  />
                );
              })}
            </PhoneCarousel>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export const Collabs = ({ slice }) => {
  const { color_scheme, show_background_static_video } = slice?.primary || {};
  const items = slice?.items || [];

  return (
    <ActiveSlideProvider>
      <CollabsContent
        items={items}
        color_scheme={color_scheme}
        show_background_static_video={show_background_static_video}
      />
    </ActiveSlideProvider>
  );
};

export const query = graphql`
  fragment HomepageDataBodySocialVideos on PrismicHomepageDataBodySocialVideos {
    primary {
      color_scheme
      show_background_static_video
    }
    items {
      heading {
        richText
        text
      }
      copy {
        richText
        text
      }
      video_url
      image {
        url
        alt
      }
      stat_1
      stat_2
      stat_3
    }
  }
`;
